/**
 * PasoActividades — Paso 4 del IS / Paso 2 del AC. Array editable de actividades.
 *
 * En IS: las descripciones se derivan del panel fotográfico (sin upload de foto aquí).
 * En AC: uso manual libre.
 */
import { Plus, Trash2, Copy } from "lucide-react";
import { Button } from "@/shared/ui/button.js";
import type { IsActividad } from "../../types.js";
import { LabeledTextarea, labelClass } from "../ie/fields.js";

export function PasoActividades(props: {
  value: IsActividad[];
  onChange: (next: IsActividad[]) => void;
  onError?: (msg: string | null) => void;
  disabled?: boolean;
  /** IS only: pull descriptions from the panel fotográfico as new actividades. */
  onJalarPanel?: () => void;
}) {
  const { value, onChange, disabled, onJalarPanel } = props;

  function agregar() {
    onChange([...value, { descripcion: "" }]);
  }

  function quitar(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function setDescripcion(index: number, descripcion: string) {
    onChange(value.map((a, i) => (i === index ? { ...a, descripcion } : a)));
  }

  return (
    <div className="space-y-4">
      {onJalarPanel && (
        <div className="flex flex-col gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onJalarPanel}
            className="gap-1.5 self-start font-mono text-[0.625rem] uppercase tracking-[0.1em]"
          >
            <Copy className="size-3.5" aria-hidden />
            Copiar descripciones del panel
          </Button>
          <p className="text-xs text-muted-foreground">
            Agrega las descripciones del panel fotográfico como actividades. No reemplaza las que ya
            escribiste — las nuevas se suman y quedan editables.
          </p>
        </div>
      )}
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin actividades todavía. Agrega la primera con el botón de abajo.
        </p>
      ) : (
        <ul className="space-y-4">
          {value.map((actividad, i) => (
            <li key={i} className="space-y-3 rounded-lg border border-border bg-background/60 p-4">
              <div className="flex items-center justify-between">
                <span className={labelClass}>Actividad {i + 1}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => quitar(i)}
                  className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em] text-destructive"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  Quitar
                </Button>
              </div>
              <LabeledTextarea
                id={`act-${i}-descripcion`}
                label="Descripción"
                value={actividad.descripcion}
                onChange={(v) => setDescripcion(i, v)}
                disabled={disabled}
                placeholder="Qué se hizo en esta actividad"
              />
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={agregar}
        className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
      >
        <Plus className="size-3.5" aria-hidden />
        Agregar actividad
      </Button>
    </div>
  );
}

